/*!
 * @license
 * Copyright Squiz Australia Pty Ltd. All Rights Reserved.
 */

import {
  BUTTON_FORMAT_SOFT,
  BUTTON_FORMAT_STRONG,
  BUTTON_THEME_DANGER,
  BUTTON_THEME_DEFAULT,
  Button,
  WIZARD_DIRECTION_NEXT,
  WizardStepProps,
} from '@squiz/sds';
import React, { useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useFileStore } from '../../../../../../../../Services/file-store';
import { FILE_EXPLORER_STRINGS } from '../../../../FileExplorer.constants';
import { useFileExplorerStore } from '../../../../FileExplorer.state';

export const DeleteItemDialogContent = (props: WizardStepProps): JSX.Element => {
  const { close, setButtons, setButtonKeys, updateStep } = props;
  const { t } = useTranslation();
  const { selectedRows, fileExplorerPosition, setDirectoryContents, setClearSelectedRows } = useFileExplorerStore();
  const { removeItems, loadingState, getDirectoryContents } = useFileStore();

  const handleFileRemoval = useCallback(async () => {
    const items = selectedRows.map((row) => ({ id: row.id, type: row.type }));
    const { errors } = await removeItems({ items });
    const fileList = await getDirectoryContents({ source: fileExplorerPosition });
    setDirectoryContents(fileList.data);

    if (errors > 0) {
      return updateStep(WIZARD_DIRECTION_NEXT, { errors });
    }
    close();
    setClearSelectedRows(true);
  }, [
    close,
    fileExplorerPosition,
    getDirectoryContents,
    removeItems,
    selectedRows,
    setClearSelectedRows,
    setDirectoryContents,
    updateStep,
  ]);

  const labels = useMemo(() => {
    return {
      cancel: t(FILE_EXPLORER_STRINGS.cancel),
      delete: t(FILE_EXPLORER_STRINGS.delete),
      deleteFilesFolders: t(FILE_EXPLORER_STRINGS.deleteFilesFolders),
      deleteItemsDialogText: t(FILE_EXPLORER_STRINGS.deleteItemsDialogText, { count: selectedRows.length }),
    };
  }, [selectedRows, t]);

  useEffect(() => {
    const cancelButton = (
      <Button
        classes="file-explorer__delete-dialog-btn--cancel"
        format={BUTTON_FORMAT_SOFT}
        onClick={(): void => close()}
        text={labels.cancel}
        theme={BUTTON_THEME_DEFAULT}
      />
    );

    const deleteButton = (
      <Button
        classes="file-explorer__delete-dialog-btn--delete"
        format={BUTTON_FORMAT_STRONG}
        isLoading={loadingState.removeItemsLoading}
        onClick={async (): Promise<void> => handleFileRemoval()}
        text={labels.delete}
        theme={BUTTON_THEME_DANGER}
      />
    );

    setButtonKeys([`cancel`, `delete`]);
    setButtons([cancelButton, deleteButton]);
  }, [close, handleFileRemoval, labels, loadingState, setButtonKeys, setButtons]);

  return (
    <div className="file-explorer__dialog-content delete-step-1">
      <div className="sds-dialog__heading custom">
        <span>{labels.deleteFilesFolders}</span>
      </div>
      <p className="file-explorer__dialog-text">{labels.deleteItemsDialogText}</p>
    </div>
  );
};
